import React, { useEffect, useState } from "react";
import ReactDropzone, { useDropzone } from "react-dropzone";
import { size, map, get, isEmpty } from "lodash";
import DoneIcon from "@material-ui/icons/Done";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import CloudUploadIcon from "@material-ui/icons/CloudUpload";
import TimerIcon from "@material-ui/icons/Timer";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import ErrorIcon from "@material-ui/icons/Error";
import { Tooltip, makeStyles, Grid, Box } from "@material-ui/core";
import { StyledButton } from "../../components/CustomButtons/StyledButton";
import loading_image from "../../assets/img/loading.gif";
import { Button, Typography } from "@material-ui/core";
import { FormButton } from "./FormButton";
import LinearProgress from '@mui/material/LinearProgress'

const MAX_FILE_SIZE_BYTES = 5242880
const MIN_FILE_SIZE_BYTES = 204800
const DEFAULT_MAX_FILE_SIZE_BYTES = 52428800
const DEFAULT_MIN_FILE_SIZE_BYTES = 0

const useStyles = makeStyles({
  thumbsContainer: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    marginTop: 16,
    maxHeight: "125px",
    overflow: "auto",
  },
  thumb: {
    display: "inline-flex",
    borderRadius: 2,
    border: "1px solid #eaeaea",
    width: 100,
    height: 100,
    padding: 4,
    boxSizing: "border-box",
    position: "relative",
    margin: "8px",
  },
  thumbInner: {
    display: "flex",
    minWidth: 0,
    overflow: "hidden",
  },
  img: {
    display: "block",
    width: "auto",
    height: "100%",
  },
  file_annotation_style: {
    position: "absolute",
    top: "10px",
    left: "10px",
    background: "white",
  },
  file_loading_style: {
    position: "absolute",
    right: "10px",
    bottom: "10px",
  },
  file_icon: {
    transform: "rotate(308deg)",
    color: "black",
    fontSize: "30px",
  },
  dropzone: {
    textAlign: "center",
  },
  flexBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between ",
    width: "300px",
    paddingLeft: "5px",
    paddingRight: "5px",
    paddingTop: "15px",
    paddingBottom: "15px",
  },
  flex1: {
    position: "absolute",
    left: "2%",
  },
  flex2: {
    position: "absolute",
    left: "15%",
    width: "75%",
    textTransform: "none",
    marginRight: "0px",
  },
  thumbsBriefText: {
    fontWeight: 400,
    fontSize: "13px",
    color: "#000",
  },
  thumbsWrapper: {
    padding: "10px",
    display: "flex",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
  },
  imagesWrapper: {
    padding: "10px",
  },
  briefWrapper: {
    padding: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  rejected: {
    fontSize: "20px",
    color: "red",
    paddingBottom: "10px"
  }
});

export const PhotographUploader = ({
  onChange,
  maxFileLimit,
  uploadMessage,
  fileAnnotations,
  numFilesCompleted,
  isEditorForPhotoArt,
  isEditorForLogoUpload,
  numFilesSelectedForUpload,
  setShowRejectionsModal,
  setRejectedFiles,
  rejectedFiles,
  validate,
}) => {
  const classes = useStyles();
  const [files, setFiles] = useState([]);
  const progress = (numFilesCompleted / numFilesSelectedForUpload) * 100
  const { getRootProps, getInputProps } = useDropzone({
    accept: "image/*",
    maxFiles: maxFileLimit || null,
    multiple: !isEditorForPhotoArt,
    accept: "image/jpg, image/jpeg",
    maxSize: MAX_FILE_SIZE_BYTES,
    minSize: MIN_FILE_SIZE_BYTES,
    onDrop: (acceptedFiles) => {
      setFiles(
        acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: window.URL.createObjectURL(file),
          }),
        ),
      );
      onChange({ files: acceptedFiles });
    },
  });
  
  const renderThumbsBrief = () => {
    return (
      <>
        {!isEditorForPhotoArt &&
         <span className={classes.thumbsBriefText}>
           ({size(files)} photograph{size(files) !== 1 && "s"} selected for upload.)
         </span>
        }
        {isEditorForPhotoArt &&
         <span className={classes.thumbsBriefText}>
           ({size(files) >= 1 ? 1 : 0} photograph{size(files) !== 1 && "s"} selected for upload.)
         </span>
        }
      </>
    )
  }

  const renderThumbsFull = () => {
    return (
      <>
        {map(files, function (file) {
          const fileAnnotation = get(fileAnnotations, file.path);
          let icon = null;
          let in_progress = false;
          switch (get(fileAnnotation, "status")) {
            case "pending":
              icon = <TimerIcon />;
              in_progress = true;
              break;
            case "scaling":
              icon = <HourglassEmptyIcon />;
              in_progress = true;
              break;
            case "uploading":
              icon = <CloudUploadIcon />;
              in_progress = true;
              break;
            case "duplicate":
              icon = <DoneIcon />;
              in_progress = false;
              break;
            case "success":
              icon = <DoneIcon />;
              in_progress = false;
              break;
            case "failed":
              icon = <ErrorIcon />;
              in_progress = false;
              break;
          }

          return (
            <div className={classes.thumb} key={file.name}>
              <div className={classes.thumbInner}>
                <img src={file.preview} className={classes.img} />
              </div>
              {fileAnnotation && (
                <>
                  <div className={classes.file_annotation_style}>{icon}</div>
                  {in_progress && (
                    <div className={classes.file_loading_style}>
                      <img src={loading_image} />
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </>
    );
  };

  useEffect(
    () => () => {
      // Make sure to revoke the data uris to avoid memory leaks
      files.forEach((file) => window.URL.revokeObjectURL(file.preview));
    },
    [files],
  );

  const handleInnerClick = (e) => {
    e.stopPropagation()
    setShowRejectionsModal(true)
  };

  const renderFileRejections = (fileRejections) => {
    setRejectedFiles(fileRejections)

    return (
      <div>
        <div className={ classes.rejected }>
          { fileRejections.length } invalid { fileRejections.length == 1 ? 'file' : 'files'} rejected.
        </div>
        <div>
          Please ensure selected files are of type JPG/JPEG,
        </div>
        <div>
          and are between 200KB and 5MB in size. <a onClick={ (e) => handleInnerClick(e) }> more info </a>
        </div>
      </div>
    )
  }

  return (
    <Grid item xs={12} className={classes.buttonWrapper}>
      <Box
        sx={{
          width: "90%",
          margin: "0 auto",
          height: "200px",
          textAlign: 'center',
          alignItems: "center",
          padding: "10px",
          borderWidth: "2px",
          borderRadius: "2px",
          borderColor: "rgb(204, 204, 204)",
          borderStyle: "dashed",
          backgroundColor: "rgb(250, 250, 250)",
          color: "#606060",
          outline: "none",
          transition: "border 0.24s ease-in-out 0s",
          cursor: "pointer",
          fontWeight: "500",
          fontSize: "12px",
        }}
      >
        <ReactDropzone
          disabled={numFilesCompleted !== numFilesSelectedForUpload}
          accept="image/jpg, image/jpeg"
          maxSize={ validate ? MAX_FILE_SIZE_BYTES : DEFAULT_MAX_FILE_SIZE_BYTES }
          minSize={ validate ? MIN_FILE_SIZE_BYTES : DEFAULT_MIN_FILE_SIZE_BYTES }
          onDrop={(acceptedFiles, fileRejections) => {
            setFiles(
              acceptedFiles.map((file) =>
                Object.assign(file, {
                  preview: window.URL.createObjectURL(file),
                }),
              ),
            );
            onChange({ files: acceptedFiles, rejections: fileRejections })
          }}
        >
          {({ getRootProps, getInputProps, isDragActive, fileRejections }) => (
            <Box
              {...getRootProps()}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
              }}
            >
              <input {...getInputProps()} />
              {isDragActive ? (
                <Typography variant="p">
                  Release to drop the _ here ...
                </Typography>
              ) : (
                <Box
                  sx={{
                    gap: 1,
                    display: "flex",
                    alignItems: "center",
                    flexDirection: "column",
                  }}
                >
                  <Typography variant='p'>
                    { uploadMessage }
                  </Typography>
                  {!isEditorForLogoUpload && 
                   <Box className={classes.briefWrapper}>{renderThumbsBrief()}</Box>
                  }
                  <Grid item xs={12} className={classes.buttonWrapper}>
                    { numFilesSelectedForUpload > 0 && (
                      <div>
                        <LinearProgress variant="determinate" value={progress} />
                        {!isEditorForPhotoArt && `${numFilesCompleted} of ${numFilesSelectedForUpload} completed.`}
                      </div>
                    )}
                  </Grid>
                  <Grid item xs={12} className={classes.buttonWrapper}>
                  { rejectedFiles?.length > 0 && renderFileRejections(fileRejections) }
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </ReactDropzone>
      </Box>
    </Grid>
  )
}
